/**
 * Checkout Hook
 * Initiates Stripe Checkout sessions via the shared checkout API
 */

import { useState, useCallback } from "react";

type Product =
  | "claudette"
  | "cvplus"
  | "bayit-premium"
  | "olorin-fan"
  | "olorin-superfan"
  | "olorin-starter"
  | "olorin-growth";

interface CheckoutState {
  isLoading: boolean;
  error: string | null;
}

interface CheckoutSessionResponse {
  url: string;
}

const getCheckoutApiUrl = (): string => {
  const url =
    typeof window !== "undefined"
      ? ((window as unknown as Record<string, unknown>).__CHECKOUT_API_URL__ as
          | string
          | undefined)
      : undefined;

  return url || process.env.REACT_APP_CHECKOUT_API_URL || "";
};

/**
 * Hook for initiating Stripe Checkout sessions
 * Calls the shared checkout API and redirects to Stripe-hosted checkout
 */
export const useCheckout = (): {
  initiateCheckout: (
    product: Product,
    email: string,
    locale?: string,
  ) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
} => {
  const [state, setState] = useState<CheckoutState>({
    isLoading: false,
    error: null,
  });

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const initiateCheckout = useCallback(
    async (product: Product, email: string, locale?: string): Promise<void> => {
      const apiUrl = getCheckoutApiUrl();

      if (!apiUrl) {
        setState({
          isLoading: false,
          error: "Checkout service is not configured",
        });
        return;
      }

      setState({ isLoading: true, error: null });

      try {
        const response = await fetch(`${apiUrl}/checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ product, email, locale }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const message =
            (errorData as { error?: string }).error ||
            `Checkout failed (${response.status})`;
          setState({ isLoading: false, error: message });
          return;
        }

        const data = (await response.json()) as CheckoutSessionResponse;

        if (!data.url) {
          setState({ isLoading: false, error: "Invalid checkout response" });
          return;
        }

        window.location.href = data.url;
      } catch {
        setState({
          isLoading: false,
          error: "Unable to connect to checkout service",
        });
      }
    },
    [],
  );

  return {
    initiateCheckout,
    isLoading: state.isLoading,
    error: state.error,
    clearError,
  };
};

export type { Product as CheckoutProduct };
export default useCheckout;
